import { c as _c } from "react/compiler-runtime";
import { useEffect } from 'react';
import classnames from 'classnames/bind';
import ErrorBoundary from '@sbt-web/error-boundary';
import type { Pulse } from '@sbt-web/tracking';
import { Button } from '@sbt-web/ui';
import { STARS_NUMBER } from './constants';
import { Message } from './Message';
import { Stars } from './Stars';
import { viewTrackingAction } from './tracking';
import styles from './UserRating.module.scss';
const cx = classnames.bind(styles);
interface CommonProps {
  userId: string;
  score?: number;
  classes?: string[];
  pulseInstance?: Pulse;
  reviewCount?: number;
  starColor?: string;
  showScore?: boolean;
  showRating?: boolean;
}
interface ShowReviewCountProps {
  showReviewCount: boolean;
  onReviewCountClick: () => void;
}
interface WithoutShowReviewCountProps {
  showReviewCount?: never;
  onReviewCountClick?: never;
}
export type UserRatingProps = CommonProps & (ShowReviewCountProps | WithoutShowReviewCountProps);
export const isViewableRating = (reviewCount?: number, score?: number): boolean => {
  if (!reviewCount || !score) {
    return false;
  }
  return reviewCount >= 1;
};
export const getNormalizedScore = (score: number) => new Intl.NumberFormat('it-IT', {
  maximumSignificantDigits: 2,
  minimumSignificantDigits: 2
}).format(score * STARS_NUMBER);
const getReviewCountText = (reviewCount?: number) => {
  if (!reviewCount) {
    return null;
  }
  return `${reviewCount} ${reviewCount > 1 ? 'recensioni' : 'recensione'}`;
};
const UserRatingComponent = t0 => {
  const $ = _c(31);
  const {
    userId,
    classes,
    pulseInstance,
    score,
    reviewCount,
    showReviewCount: t1,
    starColor,
    showScore: t2,
    showRating,
    onReviewCountClick
  } = t0;
  const showReviewCount = t1 === undefined ? false : t1;
  const showScore = t2 === undefined ? true : t2;
  let t3;
  if ($[0] !== classes) {
    t3 = cx(styles.rating, classes);
    $[0] = classes;
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  const wrapperClasses = t3;
  const shouldShowRating = showRating ?? isViewableRating(reviewCount, score);
  let t4;
  let t5;
  if ($[2] !== pulseInstance || $[3] !== reviewCount || $[4] !== score || $[5] !== userId) {
    t4 = () => {
      if (pulseInstance) {
        viewTrackingAction({
          pulseInstance,
          userId,
          score,
          reviewCount
        });
      }
    };
    t5 = [pulseInstance, userId, score, reviewCount];
    $[2] = pulseInstance;
    $[3] = reviewCount;
    $[4] = score;
    $[5] = userId;
    $[6] = t4;
    $[7] = t5;
  } else {
    t4 = $[6];
    t5 = $[7];
  }
  useEffect(t4, t5);
  if (!shouldShowRating) {
    let t6;
    if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
      t6 = <Message text="Nessuna recensione" />;
      $[8] = t6;
    } else {
      t6 = $[8];
    }
    let t7;
    if ($[9] !== wrapperClasses) {
      t7 = <div className={wrapperClasses}>{t6}</div>;
      $[9] = wrapperClasses;
      $[10] = t7;
    } else {
      t7 = $[10];
    }
    return t7;
  }
  if (score) {
    let t6;
    if ($[11] !== score) {
      t6 = getNormalizedScore(score);
      $[11] = score;
      $[12] = t6;
    } else {
      t6 = $[12];
    }
    const normalizedScore = t6;
    let t7;
    if ($[13] !== reviewCount) {
      t7 = getReviewCountText(reviewCount);
      $[13] = reviewCount;
      $[14] = t7;
    } else {
      t7 = $[14];
    }
    const reviewCountText = t7;
    let t8;
    if ($[15] !== normalizedScore || $[16] !== showScore) {
      t8 = showScore && <span aria-hidden="true" className={styles.score}>{normalizedScore}</span>;
      $[15] = normalizedScore;
      $[16] = showScore;
      $[17] = t8;
    } else {
      t8 = $[17];
    }
    let t9;
    if ($[18] !== normalizedScore || $[19] !== score || $[20] !== starColor) {
      t9 = <Stars score={score} textualScore={normalizedScore} color={starColor} />;
      $[18] = normalizedScore;
      $[19] = score;
      $[20] = starColor;
      $[21] = t9;
    } else {
      t9 = $[21];
    }
    let t10;
    if ($[22] !== onReviewCountClick || $[23] !== reviewCountText || $[24] !== showReviewCount) {
      t10 = showReviewCount && reviewCountText && <Button design="text" size="small" onClick={onReviewCountClick} aria-label="Vedi tutte le recensioni">{reviewCountText}</Button>;
      $[22] = onReviewCountClick;
      $[23] = reviewCountText;
      $[24] = showReviewCount;
      $[25] = t10;
    } else {
      t10 = $[25];
    }
    let t11;
    if ($[26] !== t10 || $[27] !== t8 || $[28] !== t9 || $[29] !== wrapperClasses) {
      t11 = <div className={wrapperClasses}>{t8}{t9}{t10}</div>;
      $[26] = t10;
      $[27] = t8;
      $[28] = t9;
      $[29] = wrapperClasses;
      $[30] = t11;
    } else {
      t11 = $[30];
    }
    return t11;
  }
  return null;
};
export const UserRating = props => {
  const $ = _c(2);
  let t0;
  if ($[0] !== props) {
    t0 = <ErrorBoundary><UserRatingComponent {...props} /></ErrorBoundary>;
    $[0] = props;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  return t0;
};